import { i as isHlsMediaType } from './media-type.js';
import { N as NetworkError, D as DecodeError, l as loadHlsMediaPlaylist } from './hls-media-playlist.js';
import { Parser } from 'm3u8-parser';
import { f as fetchText, d as dimensionsToOrientation } from './fetch.js';
import { t as toError } from './error.js';

// Based on hls.js/src/utils/codecs.js.
// from https://mp4ra.org/#/codecs
const sampleEntryCodesISO = {
  audio: {
    a3ds: true,
    'ac-3': true,
    'ac-4': true,
    alac: true,
    alaw: true,
    dra1: true,
    'dts+': true,
    'dts-': true,
    dtsc: true,
    dtse: true,
    dtsh: true,
    'ec-3': true,
    enca: true,
    g719: true,
    g726: true,
    m4ae: true,
    mha1: true,
    mha2: true,
    mhm1: true,
    mhm2: true,
    mlpa: true,
    mp4a: true,
    'raw ': true,
    Opus: true,
    samr: true,
    sawb: true,
    sawp: true,
    sevc: true,
    sqcp: true,
    ssmv: true,
    twos: true,
    ulaw: true
  },
  video: {
    avc1: true,
    avc2: true,
    avc3: true,
    avc4: true,
    avcp: true,
    av01: true,
    drac: true,
    dvav: true,
    dvhe: true,
    encv: true,
    hev1: true,
    hvc1: true,
    mjp2: true,
    mp4v: true,
    mvc1: true,
    mvc2: true,
    mvc3: true,
    mvc4: true,
    resv: true,
    rv60: true,
    s263: true,
    svc1: true,
    svc2: true,
    'vc-1': true,
    vp08: true,
    vp09: true
  }
};
function isCodecType(codec, type) {
  const typeCodes = sampleEntryCodesISO[type];
  return !!typeCodes && typeCodes[codec.slice(0, 4)] === true;
}

/**
 * Selected information from an HLS master playlist.
 */

/**
 * Fetch and parse a HLS master playlist.
 *
 * Note: Multiple renditions are not supported. Only the default rendition is
 * used.
 */
async function loadHlsMasterPlaylist(url, options) {
  let data;

  try {
    data = await fetchText(url, options);
  } catch (e) {
    const error = toError(e);

    if (error.name === 'AbortError') {
      throw error;
    }

    throw new NetworkError(`Failed to fetch master playlist: ${url}`, error);
  }

  return parseHlsMasterPlaylist(url, data);
}
/**
 * Parse the raw text data of an HLS master playlist.
 */

function parseHlsMasterPlaylist(baseUrl, data) {
  var _manifest$playlists$m, _manifest$playlists;

  const parser = new Parser();

  try {
    parser.push(data);
    parser.end();
  } catch (e) {
    throw new DecodeError('Failed to parse media playlist.', toError(e));
  }

  const manifest = parser.manifest;
  const subtitleGroupIds = new Set();
  let isAudioOnly = true;
  let orientation; // A map() with side effects...

  const levels = (_manifest$playlists$m = manifest == null ? void 0 : (_manifest$playlists = manifest.playlists) == null ? void 0 : _manifest$playlists.map(level => {
    var _level$attributes$COD, _level$attributes$COD2;

    const subtitleGroupId = level.attributes.SUBTITLES;

    if (subtitleGroupId !== undefined) {
      subtitleGroupIds.add(subtitleGroupId);
    } // Any codec not an audio codec?


    const codecs = (_level$attributes$COD = (_level$attributes$COD2 = level.attributes.CODECS) == null ? void 0 : _level$attributes$COD2.split(',')) != null ? _level$attributes$COD : [];

    if (isAudioOnly) {
      for (let i = 0; i < codecs.length; ++i) {
        if (!isCodecType(codecs[i], 'audio')) {
          isAudioOnly = false;
          break;
        }
      }
    }

    const quality = attributesToQuality(level.attributes);

    if (orientation === undefined && quality.width !== undefined) {
      orientation = dimensionsToOrientation(quality.width, quality.height);
    }

    return {
      quality,
      url: new URL(level.uri, baseUrl).toString()
    };
  })) != null ? _manifest$playlists$m : [];
  const subtitles = getSubtitlesConfigs(manifest, baseUrl, Array.from(subtitleGroupIds));
  return {
    isAudioOnly,
    levels,
    orientation,
    subtitles
  };
}
/**
 * Condense a list of levels to a list of unique qualities with correct quality
 * level index. Qualities might be equal between levels with different codecs
 * (Torgeir: Would they really? Wouldn't they have different bitrates or so?).
 *
 * The quality objects are not replaced, so `levels[0].quality` is the first
 * quality, and will get its quality level assigned in this method.
 */

function levelsToQualities(levels) {
  const qualities = removeDuplicateQualities(levels.map(level => level.quality)); // Sort and assign quality level in correct order.

  qualities.sort((a, b) => a.bitrate - b.bitrate);
  qualities.forEach((q, index) => q.level = index);
  return qualities;
}
/**
 * Find the level which best matches the given bitrate.
 */

function findNearestLevel(levels, bitrate) {
  let nearestDistance = Number.POSITIVE_INFINITY;
  let nearestLevelIndex = 0;
  levels.forEach((level, index) => {
    const distance = Math.abs(level.quality.bitrate - bitrate);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestLevelIndex = index;
    }
  });
  return levels[nearestLevelIndex];
}
/**
 * Returns the quality properties of a video or audio playlist based on its
 * attributes. The quality level index is set to -1 because it's unknown
 * outside the context of all other levels.
 *
 * Note: This function can't be exported to the final bundle because the
 * Attributes type isn't exported (defined in custom d.ts file).
 */

function attributesToQuality(attributes) {
  var _ref, _attributes$BANDWIDTH;

  const averageBitrate = attributes['AVERAGE-BANDWIDTH'] ? Number.parseInt(attributes['AVERAGE-BANDWIDTH'], 10) : undefined;
  const quality = {
    level: -1,
    bitrate: (_ref = (_attributes$BANDWIDTH = attributes.BANDWIDTH) != null ? _attributes$BANDWIDTH : averageBitrate) != null ? _ref : 0,
    averageBitrate,
    frameRate: attributes['FRAME-RATE']
  };
  const resolution = attributes.RESOLUTION;

  if (resolution !== undefined) {
    quality.width = resolution.width;
    quality.height = resolution.height;
  }

  return quality;
}
/**
 * Remove duplicate qualities while keeping the order of the qualities.
 */


function removeDuplicateQualities(qualities) {
  const seen = {};
  return qualities.filter(q => {
    const asString = JSON.stringify(q);

    if (seen[asString]) {
      return false;
    }

    seen[asString] = true;
    return true;
  });
}
/**
 * Returns subtitles tracks from a master playlist as SubtitleConfig objects.
 *
 * While subtitleGroupIds is a Set, multiple groups must be equal except for
 * the encoding. We only support WebVTT, which seems to be the case for HLS as
 * well. Until there's a concrete example of multiple groups with a clear
 * encoding distinction, the set is expected to contain zero or one group ID,
 * and the subtitles are assumed to be WebVTT.
 * https://datatracker.ietf.org/doc/html/draft-pantos-hls-rfc8216bis-09#section-4.4.6.1.1
 *
 * Note: This function can't be exported to the final bundle because the
 * Manifest type isn't exported (defined in custom d.ts file).
 */


function getSubtitlesConfigs(manifest, baseUrl, groupIds) {
  const subtitles = [];

  if (groupIds.length === 0) {
    return undefined;
  } // Assume the first group is the WebVTT group. See the function docs above.


  const subtitleGroup = manifest.mediaGroups.SUBTITLES[groupIds[0]];
  Object.keys(subtitleGroup).forEach(label => {
    const track = subtitleGroup[label];
    const {
      language,
      autoselect,
      forced,
      characteristics,
      default: defaultOn,
      uri
    } = track;
    subtitles.push({
      id: `hls-${language}-${label}`.replace(/[^-\w]+/, ''),
      label,
      language,
      variant: characteristics,
      // The flags are not validated. The subtitle controls need to do the
      // right thing.
      autoselect: autoselect != null ? autoselect : false,
      defaultOn: defaultOn != null ? defaultOn : false,
      forced: forced != null ? forced : false,
      src: new URL(uri, baseUrl).toString()
    });
  });
  return subtitles.length === 0 ? undefined : subtitles;
}

class HlsMediaInfo {
  constructor(mediaInfo) {
    this.id = void 0;
    this.url = void 0;
    this.type = 'hls';
    this.mediaType = void 0;
    this.qualities = void 0;
    this.firstQuality = void 0;
    this.masterPlaylist = void 0;
    this.mediaPlaylist = void 0;

    if (mediaInfo.mediaType === undefined || !isHlsMediaType(mediaInfo.mediaType)) {
      throw new Error('Bad use of HlsMediaInfo. Not a HLS media type.');
    }

    this.id = mediaInfo.id;
    this.url = mediaInfo.url;
    this.mediaType = mediaInfo.mediaType;
  } // Media info:


  get isAudioOnly() {
    var _this$masterPlaylist$, _this$masterPlaylist;

    return (_this$masterPlaylist$ = (_this$masterPlaylist = this.masterPlaylist) == null ? void 0 : _this$masterPlaylist.isAudioOnly) != null ? _this$masterPlaylist$ : false;
  }

  get videoOrientation() {
    var _this$masterPlaylist2;

    return (_this$masterPlaylist2 = this.masterPlaylist) == null ? void 0 : _this$masterPlaylist2.orientation;
  } // HLS media playlist details:


  get playlistDuration() {
    var _this$mediaPlaylist$p, _this$mediaPlaylist;

    return (_this$mediaPlaylist$p = (_this$mediaPlaylist = this.mediaPlaylist) == null ? void 0 : _this$mediaPlaylist.playlistDuration) != null ? _this$mediaPlaylist$p : 0;
  }

  get averageSegmentDuration() {
    var _this$mediaPlaylist$a, _this$mediaPlaylist2;

    return (_this$mediaPlaylist$a = (_this$mediaPlaylist2 = this.mediaPlaylist) == null ? void 0 : _this$mediaPlaylist2.averageSegmentDuration) != null ? _this$mediaPlaylist$a : 0;
  }

  get programDateTime() {
    var _this$mediaPlaylist3;

    const pdt = (_this$mediaPlaylist3 = this.mediaPlaylist) == null ? void 0 : _this$mediaPlaylist3.programDateTime;
    return pdt ? pdt.getTime() / 1000 : undefined;
  }
  /**
   * Loads and stores the master playlist. Required to complete the media info.
   */


  async loadMasterPlaylist(options) {
    this.masterPlaylist = await loadHlsMasterPlaylist(this.url, options);
    this.firstQuality = this.findLevel().quality;
    this.qualities = levelsToQualities(this.masterPlaylist.levels);
  }
  /**
   * Loads and stores a media playlist, referenced in the master playlist.
   */


  async loadMediaPlaylist(options) {
    const level = this.findLevel(options == null ? void 0 : options.bitrate);
    this.mediaPlaylist = await loadHlsMediaPlaylist(level.url, options);
  }
  /**
   * Returns the first level or the level closest to the given bitrate.
   */


  findLevel(bitrate) {
    if (this.masterPlaylist === undefined) {
      throw new Error('Have to load master playlist first.');
    }

    if (this.masterPlaylist.levels.length === 0) {
      throw new Error('Master playlist has no quality levels.');
    }

    if (bitrate === undefined) {
      return this.masterPlaylist.levels[0];
    }

    return findNearestLevel(this.masterPlaylist.levels, bitrate);
  }

}

export { HlsMediaInfo as H };
